import { getBackendUrl } from "@Utils";
import { sanitizeHtml } from "@Utils/sanitize";
import { useTranslation } from "@i18n";
import { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import "./editor.css";
import {
  getCommentsData,
  saveCommentsData,
  deleteCommentsData,
  isIssueRequiredPermission,
} from "@Features/issues/state/bcfData.actions";
import CommentsAdd from "./CommentsAdd";
import ButtonComponent from "../../cubeComponent/form/ButtonComponent";
import { BUTTON_TYPES } from "../../cubeComponent/constants/globalVariable";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import ToolTipComponent from "../../cubeComponent/html/tooltip";
import { convertDate } from "../../services/luxonHelper";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axiosWrapper from "../../services/axiosWrapper";
import type { CreateOfficialCommentResponse } from "@ServerTypes/issues/issues.schema";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import _ from "lodash";
import IconComponent from "@CubeComponents/icons";
import { SimpleEditor } from "../../cubeComponent/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { extractTempKeysFromHtml, replaceBase64WithTempKeys } from "@Helpers/htmlUtils";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import { folderPermissions } from "@Helpers/constants";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { CubeGlobal, HoopsViewerContext } from "@Features/viewer-core";
import { dispatchModelLoadingToast } from "@Actions/Viewer";
import type React from "react";
import type { ComponentProps } from "react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_COMMENT, RESOURCE_CHANGE_PERMISSION } = folderPermissions();

// IssueItem.comments is z.unknown() server-side; modelled here
type CommentEntry = {
  _id: ObjectIdString;
  title: string;
  snapshot?: string | null;
  viewpoint?: string | null;
  createdBy?: { _id?: string; fullName?: string };
  createdDate?: string | number;
};

/** The `{ value }` wrapper `handleSaveCommentClick` probes for on the snapshot/viewpoint edit drafts. */
type WrappedDraft = { value?: string };

/**
 * Mounted from the issue side panel (`viewIssue`) and from the standalone issue details page, which
 * pass overlapping subsets of these props — hence everything but `issueId` is optional. `issueId`
 * arrives as a plain string and is asserted to `ObjectIdString` at each request boundary below.
 */
type CommentsListProps = {
  issueId: string;
  /** Viewpoint selection entries. `nodeId` is `"<model>_<node>"`; `elementsContent` drops the first segment. */
  elements?: { nodeId: string }[];
  fileVersionId?: string;
  handleAddComment?: (issueIndex?: number) => void;
  issueIndex?: number;
  showAddComment?: boolean;
  pdfViewer?: boolean;
  issueIsStandalone?: boolean;
  offialCommentId?: string;
};

/** Args of `SelectDropDown`'s `select` callback; the interface itself is not exported. */
type SelectDropDownSelectArgs = Parameters<NonNullable<ComponentProps<typeof SelectDropDown>["select"]>>[0];

function CommentsList(props: CommentsListProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showRichEditor, setShowRichEditor] = useState(false);
  const [editCommentsOperation, setEditCommentsOperation] = useState<Record<string, string>>({});
  const [savedCommentsTitleOperation, setSavedCommentsTitleOperation] = useState<Record<string, string>>({});
  const [savedCommentsSnapshotOperation, setSavedCommentsSnapshotOperation] = useState<Record<string, string>>({});
  const [savedCommentsViewpointOperation, setSavedCommentsViewpointOperation] = useState<
    Record<string, string | null | undefined>
  >({});
  const [offialCommentId, setOffialCommentId] = useState<string | null>(null);
  const [bcfData] = useState("");
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const issues = useSelector((state: RootState) => state.issues);
  const issueComments = (issues[props.issueId]?.comments ?? {}) as Record<string, CommentEntry>;
  const [accordionState, setAccordionState] = useState({
    comments: true,
    elements: false,
  });
  const { viewerImpl, isModelStructureReady, isSettingsApplied } = useContext(HoopsViewerContext);

  const fetchComments = useCallback(() => {
    dispatch(
      getCommentsData({
        issueId: props.issueId as ObjectIdString,
      }),
    );
  }, [props.issueId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (props.offialCommentId) {
      setOffialCommentId(props.offialCommentId);
    }
  }, [props.offialCommentId]);

  const dropDownButtonOfficialItems = [
    { text: "Edit", id: "edit", disabled: !isIssueRequiredPermission(issues[props.issueId], RESOURCE_COMMENT.id) },
    { text: "Delete", id: "delete", disabled: !isIssueRequiredPermission(issues[props.issueId], RESOURCE_COMMENT.id) },
  ];

  const dropDownButtonItems = [
    ...dropDownButtonOfficialItems,
    {
      text: "Mark as Official",
      id: "official",
      disabled: !isIssueRequiredPermission(issues[props.issueId], RESOURCE_CHANGE_PERMISSION.id),
    },
  ];

  const handleSelectedOption = (args: SelectDropDownSelectArgs, cmtId: ObjectIdString, cmtCreatedBy?: string) => {
    if (args.item.id === "edit") {
      handleEditClick(cmtId);
    } else if (args.item.id === "delete") {
      handleDeleteCommentClick(cmtId, cmtCreatedBy);
    } else if (args.item.id === "official") {
      handleOfficialCommentClick(cmtId);
    }
  };

  const userComentHeader = () => (
    <div style={{ display: "flex", alignItems: "center", columnGap: "0.625rem" }}>
      <IconComponent name="prime_comments" width="1.25rem" height="1.25rem" />
      <Text>{t("Comments")}</Text>
    </div>
  );

  const elementsHeader = () => (
    <div style={{ display: "flex", alignItems: "center", columnGap: "0.625rem" }}>
      <IconComponent name="akar-icons_shipping-box-01" width="1.25rem" height="1.25rem" />
      <Text>{t("Elements")}</Text>
    </div>
  );

  const getUserAvatar = (id?: string) => {
    return <UserNameTagComponent showAvatarOnly id={id} size="LSM" isCircle />;
  };

  const handleEditClick = (commentId: string) => {
    const comment = issueComments[commentId];
    if (!comment) return;
    setEditCommentsOperation({
      ...editCommentsOperation,
      [commentId]: comment.title,
    });
    setSavedCommentsTitleOperation({
      ...savedCommentsTitleOperation,
      [commentId]: comment.title,
    });
    if (comment.snapshot) {
      setSavedCommentsSnapshotOperation({
        ...savedCommentsSnapshotOperation,
        [commentId]: comment.snapshot,
      });
      setSavedCommentsViewpointOperation({
        ...savedCommentsViewpointOperation,
        [commentId]: comment.viewpoint,
      });
    }
  };

  const handleSaveCommentClick = (commentId: ObjectIdString) => {
    const imageTempKeys = extractTempKeysFromHtml(editCommentsOperation[commentId]!);
    const processedTitle = replaceBase64WithTempKeys(
      editCommentsOperation[commentId]!,
      (tempKey) => `${getBackendUrl()}/fileSystemServer/getCommentImage/${tempKey}`,
    );

    setSavedCommentsTitleOperation({
      ...savedCommentsTitleOperation,
      [commentId]: processedTitle,
    });
    const savedData = {
      fileVersionId: props?.fileVersionId as ObjectIdString | undefined,
      title: processedTitle!,
      issueId: props.issueId as ObjectIdString,
      // KNOWN BUG, preserved verbatim: `handleEditClick` only ever stores the plain snapshot/viewpoint
      // strings, so the `{ value }` wrapper branches below are dead. The wrapper shape is asserted
      // rather than folded into the state type; narrowing it away would change behaviour.
      snapshot:
        props.pdfViewer || props.issueIsStandalone
          ? null
          : (savedCommentsSnapshotOperation[commentId] as WrappedDraft | undefined)?.value
            ? (savedCommentsSnapshotOperation[commentId] as WrappedDraft).value
            : savedCommentsSnapshotOperation[commentId],
      viewpoint:
        props.pdfViewer || props.issueIsStandalone
          ? null
          : (savedCommentsViewpointOperation[commentId] as WrappedDraft | undefined)?.value
            ? JSON.stringify((savedCommentsViewpointOperation[commentId] as WrappedDraft).value)
            : savedCommentsViewpointOperation[commentId],
      commentId,
      bcfData: props.pdfViewer || props.issueIsStandalone ? null : JSON.stringify(bcfData),
      imageTempKeys,
    };
    dispatch(saveCommentsData(savedData));

    handleCancelClick(commentId)();
  };
  const handleDeleteCommentClick = (commentId: ObjectIdString, createdBy?: string) => {
    const Data = {
      commentId,
      createdBy,
    };
    dispatch(
      deleteCommentsData({
        ...Data,
        issueId: props.issueId as ObjectIdString,
      }),
    );
  };
  const handleOfficialCommentClick = async (commentId: ObjectIdString) => {
    const response = await axiosWrapper.post<CreateOfficialCommentResponse>(
      `${getBackendUrl()}/issues/createOfficialComment`,
      {
        issueId: props.issueId,
        commentId,
      },
    );
    if (response.data.status) {
      setOffialCommentId(commentId);
    }
  };

  const commentChangeHandler = (commentId: ObjectIdString) => (value: string) => {
    setEditCommentsOperation({
      ...editCommentsOperation,
      [commentId]: value,
    });
  };

  const saveButtonState = (commentId: ObjectIdString) => {
    const html = editCommentsOperation[commentId]!;
    const doc = new DOMParser().parseFromString(html ?? "", "text/html");
    const text = doc.body.textContent || "";
    const hasImage = doc.body.querySelector("img") !== null;
    const isUploading = doc.body.querySelector('[data-type="image-upload"]') !== null;
    if ((text.trim() !== "" || hasImage) && !isUploading) {
      return false;
    }
    return true;
  };

  const handleCancelClick = (commentId: ObjectIdString) => () => {
    const editData = { ...editCommentsOperation };
    delete editData[commentId];
    setEditCommentsOperation(editData);
  };

  const onCommentAddClick = () => {
    if (
      props.pdfViewer ||
      getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })
    ) {
      setShowRichEditor(true);
    } else {
      dispatch(dispatchModelLoadingToast());
    }
  };

  const commentHtml = (value: string) => {
    const comment = issueComments[value];
    if (!comment) return null;
    return (
      <div className="flex flex-col pb-5">
        <div className="flex flex-row gap-2">
          <div className="flex">{getUserAvatar(comment.createdBy?._id)}</div>
          <div className="flex w-[calc(100%-2.375rem)] flex-col gap-1">
            <div className="flex flex-row justify-between">
              <span className="flex flex-col justify-center text-xs">
                <span className="font-semibold">{comment.createdBy?.fullName}</span>
              </span>
              <div className="flex items-center gap-2.5 text-[0.5rem] text-[#666a84]">
                {offialCommentId === comment._id && (
                  <div className="border rounded-full mt-1">
                    <Text className="p-2 font-semibold">{t("Official")}</Text>
                  </div>
                )}
                <ToolTipComponent
                  content={convertDate(Number(comment.createdDate), "dateForTooltip")}
                  position="bottom"
                >
                  <span className="text-[0.5rem] text-[#666a84]">
                    {convertDate(Number(comment.createdDate), "dateForText")}
                  </span>
                </ToolTipComponent>
                <SelectDropDown
                  select={(args) => handleSelectedOption(args, comment._id, comment.createdBy?._id)}
                  items={offialCommentId === comment._id ? dropDownButtonOfficialItems : dropDownButtonItems}
                  cssClass="e-caret-hide bg-white border-0 p-0 pt-px flex"
                >
                  <IconComponent name="HorizontalThreeDots" width="1rem" height="1rem" />
                </SelectDropDown>
              </div>
            </div>
            <div>
              {comment.title && !Object.hasOwn(editCommentsOperation, comment._id) && (
                <SimpleEditor
                  value={sanitizeHtml(comment.title)}
                  editable={false}
                  showToolbar={false}
                  showCharCount={false}
                />
              )}
              {comment.title && Object.hasOwn(editCommentsOperation, comment._id) && (
                <div className="flex flex-col gap-5">
                  <SimpleEditor
                    onChange={commentChangeHandler(comment._id)}
                    value={sanitizeHtml(savedCommentsTitleOperation[comment._id])}
                    editable={true}
                    allowImageUpload={true}
                  />
                  <div className="flex justify-end gap-7">
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                      onClick={() => handleSaveCommentClick(comment._id)}
                      disabled={saveButtonState(comment._id)}
                    >
                      {<Text>{t("Save")}</Text>}
                    </ButtonComponent>
                    <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleCancelClick(comment._id)}>
                      {<Text>{t("Cancel")}</Text>}
                    </ButtonComponent>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  const userComentContent = () => (
    <div className="flex flex-col gap-2.5">
      <div>
        {showRichEditor && (
          <CommentsAdd
            issueIsStandalone={props.issueIsStandalone}
            issueId={props.issueId}
            fileVersionId={props?.fileVersionId}
            handleAddComment={props.handleAddComment}
            issueIndex={props.issueIndex}
            handelShowAddComment={setShowRichEditor}
            pdfViewer={props.pdfViewer}
          />
        )}
        {!showRichEditor && (!props.showAddComment ? props.showAddComment : true) && (
          <div className="mb-7 flex h-8 w-[calc(100%-0.8125rem)] items-center rounded-[1.25rem] bg-[linear-gradient(95deg,#fff_45%,#f0f5f9_129%)] px-2.5 ps-px shadow-[0.125rem_0.125rem_0.25rem_0_rgba(25,32,72,0.15)]">
            {getUserAvatar(userDetails._id)}
            <input
              className="ms-1.5 w-[calc(100%-7.6875rem)] border-0 bg-[linear-gradient(95deg,#fff_60%,#f0f5f9_150%)]"
              onClick={onCommentAddClick}
              placeholder={t("Write a comment")}
              type="text"
              disabled={!isIssueRequiredPermission(issues[props.issueId], RESOURCE_COMMENT.id)}
            />
          </div>
        )}
        {/* `IssueItem.comments` is `z.unknown()` server-side; the guard only needs its truthiness. */}
        {(issues[props.issueId]?.comments as boolean) && Object.keys(issueComments).map((value) => commentHtml(value))}
      </div>
    </div>
  );

  const elementsContent = () => {
    let uniqueNodeIds: Set<string> | string[] = new Set([]);
    if (_.get(props, "elements.length")) {
      uniqueNodeIds = [
        ...new Set(
          props.elements!.map((item) => {
            return item.nodeId.split("_").splice(1).join("_");
          }),
        ),
      ];
    }

    return (
      <div className="flex flex-col justify-center">
        {Array.from(uniqueNodeIds).length
          ? Array.from(uniqueNodeIds).map((value: string, index: number) => (
              <div key={index} className="flex h-8 items-center text-sm text-[#666a84]">
                {`${value}`}
              </div>
            ))
          : "No elements associated"}
      </div>
    );
  };

  const handleAccordionState = (type: string) => (e: React.SyntheticEvent, isExpanded: boolean) => {
    setAccordionState({
      ...accordionState,
      [type]: isExpanded,
    });
  };

  const comentAccordion = () => (
    <Accordion disableGutters expanded={accordionState.comments} onChange={handleAccordionState("comments")}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>{userComentHeader()}</AccordionSummary>
      <AccordionDetails>{userComentContent()}</AccordionDetails>
    </Accordion>
  );
  return (
    <div className="commentListAccordian flex h-auto flex-col">
      {!props.pdfViewer && (
        <Accordion disableGutters expanded={accordionState.elements} onChange={handleAccordionState("elements")}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{elementsHeader()}</AccordionSummary>
          <AccordionDetails>{elementsContent()}</AccordionDetails>
        </Accordion>
      )}
      {comentAccordion()}
    </div>
  );
}

export default CommentsList;
